import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  useCommunityAdminMembers,
  useUpdateMemberRole,
  useSuspendMember,
  useBanMember,
} from '../../hooks/use-admin';
import {
  Users,
  Crown,
  UserCog,
  Shield,
  Search,
  MoreVertical,
  PauseCircle,
  Ban,
  ChevronUp,
  ChevronDown,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';

const getRoleInfo = (role) => {
  switch (role) {
    case 'owner':
      return {
        icon: Crown,
        color: 'text-amber-400 bg-amber-500/20',
        label: 'Owner',
      };
    case 'superAdmin':
      return {
        icon: Crown,
        color: 'text-rose-400 bg-rose-500/20',
        label: 'Super Admin',
      };
    case 'admin':
      return {
        icon: UserCog,
        color: 'text-violet-400 bg-violet-500/20',
        label: 'Admin',
      };
    case 'moderator':
      return {
        icon: Shield,
        color: 'text-blue-400 bg-blue-500/20',
        label: 'Moderator',
      };
    default:
      return {
        icon: Users,
        color: 'text-zinc-400 bg-zinc-500/20',
        label: 'Member',
      };
  }
};

export default function CommunityAdminMembers() {
  const { community, userRole } = useOutletContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: null,
    member: null,
  });
  const [reason, setReason] = useState('');

  // Hooks
  const { data: membersData, isLoading } = useCommunityAdminMembers(
    community?.oid,
    {
      search: searchQuery,
      role: selectedRole !== 'all' ? selectedRole : undefined,
    }
  );
  const updateRoleMutation = useUpdateMemberRole(community?.oid);
  const suspendMutation = useSuspendMember(community?.oid);
  const banMutation = useBanMember(community?.oid);

  const members = membersData?.members || community?.members || [];

  // Filter members client-side (in case backend doesn't support filtering)
  const filteredMembers = members.filter((member) => {
    const matchesSearch =
      !searchQuery ||
      member.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.userOid?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || member.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  const handleRoleChange = async (memberOid, newRole) => {
    await updateRoleMutation.mutateAsync({ memberOid, role: newRole });
  };

  const handleSuspend = async () => {
    await suspendMutation.mutateAsync({
      memberOid: actionDialog.member.userOid,
      reason,
    });
    setActionDialog({ open: false, type: null, member: null });
    setReason('');
  };

  const handleBan = async () => {
    await banMutation.mutateAsync({
      memberOid: actionDialog.member.userOid,
      reason,
    });
    setActionDialog({ open: false, type: null, member: null });
    setReason('');
  };

  const canManageMember = (targetRole) => {
    // Role hierarchy for management permissions
    // owner(5) > superAdmin(4) > admin(3) > moderator(2) > member(1)
    const ROLE_LEVELS = {
      owner: 5,
      superAdmin: 4,
      admin: 3,
      moderator: 2,
      member: 1,
    };
    const actorLevel = ROLE_LEVELS[userRole] || 0;
    const targetLevel = ROLE_LEVELS[targetRole] || 0;
    // Can only manage users with lower role level
    return actorLevel > targetLevel;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Members</h1>
        <p className="text-zinc-400">
          Manage community members and their roles
        </p>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                'all',
                'owner',
                'superAdmin',
                'admin',
                'moderator',
                'member',
              ].map((role) => (
                <Button
                  key={role}
                  variant={selectedRole === role ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedRole(role)}
                  className={cn(
                    selectedRole === role
                      ? 'bg-violet-600 hover:bg-violet-700'
                      : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'
                  )}
                >
                  {role === 'all'
                    ? 'All'
                    : role === 'superAdmin'
                      ? 'Super Admin'
                      : role.charAt(0).toUpperCase() + role.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">
            {filteredMembers.length} Member
            {filteredMembers.length !== 1 ? 's' : ''}
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Click on a member to view actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredMembers.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No members found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMembers.map((member) => {
                const roleInfo = getRoleInfo(member.role);
                const RoleIcon = roleInfo.icon;
                const canManage = canManageMember(member.role);

                return (
                  <div
                    key={member.userOid}
                    className="flex items-center justify-between p-4 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="relative">
                        {member.avatar ? (
                          <img
                            src={member.avatar}
                            alt={member.username}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                            <span className="text-zinc-400 font-medium">
                              {member.username?.[0]?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div>
                        <p className="font-medium text-white">
                          {member.username || 'Unknown'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={cn(
                              'text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1',
                              roleInfo.color
                            )}
                          >
                            <RoleIcon className="w-3 h-3" />
                            {roleInfo.label}
                          </span>
                          <span className="text-xs text-zinc-500">
                            Joined{' '}
                            {new Date(member.joinedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-zinc-400 hover:text-white"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-zinc-900 border-zinc-800"
                        >
                          {/* Role changes - show all roles the user can change to */}
                          {/* Owner can set any role (except owner) */}
                          {userRole === 'owner' &&
                            member.role !== 'superAdmin' && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleRoleChange(member.userOid, 'superAdmin')
                                }
                                className="text-rose-400 hover:text-rose-300 hover:bg-zinc-800"
                              >
                                <Crown className="w-4 h-4 mr-2" />
                                Set as Super Admin
                              </DropdownMenuItem>
                            )}
                          {(userRole === 'owner' ||
                            userRole === 'superAdmin') &&
                            member.role !== 'admin' && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleRoleChange(member.userOid, 'admin')
                                }
                                className="text-violet-400 hover:text-violet-300 hover:bg-zinc-800"
                              >
                                <UserCog className="w-4 h-4 mr-2" />
                                Set as Admin
                              </DropdownMenuItem>
                            )}
                          {(userRole === 'owner' ||
                            userRole === 'superAdmin' ||
                            userRole === 'admin') &&
                            member.role !== 'moderator' && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleRoleChange(member.userOid, 'moderator')
                                }
                                className="text-blue-400 hover:text-blue-300 hover:bg-zinc-800"
                              >
                                <Shield className="w-4 h-4 mr-2" />
                                Set as Moderator
                              </DropdownMenuItem>
                            )}
                          {(userRole === 'owner' ||
                            userRole === 'superAdmin' ||
                            userRole === 'admin') &&
                            member.role !== 'member' && (
                              <DropdownMenuItem
                                onClick={() =>
                                  handleRoleChange(member.userOid, 'member')
                                }
                                className="text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800"
                              >
                                <Users className="w-4 h-4 mr-2" />
                                Set as Member
                              </DropdownMenuItem>
                            )}

                          <DropdownMenuSeparator className="bg-zinc-800" />

                          {/* Suspend */}
                          <DropdownMenuItem
                            onClick={() =>
                              setActionDialog({
                                open: true,
                                type: 'suspend',
                                member,
                              })
                            }
                            className="text-yellow-400 hover:text-yellow-300 hover:bg-zinc-800"
                          >
                            <PauseCircle className="w-4 h-4 mr-2" />
                            Suspend Member
                          </DropdownMenuItem>

                          {/* Ban */}
                          <DropdownMenuItem
                            onClick={() =>
                              setActionDialog({
                                open: true,
                                type: 'ban',
                                member,
                              })
                            }
                            className="text-red-400 hover:text-red-300 hover:bg-zinc-800"
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            Ban from Community
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kick/Ban Dialog */}
      <Dialog
        open={actionDialog.open}
        onOpenChange={(open) =>
          !open && setActionDialog({ open: false, type: null, member: null })
        }
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionDialog.type === 'ban' ? (
                <>
                  <Ban className="w-5 h-5 text-red-400" />
                  Ban Member
                </>
              ) : (
                <>
                  <PauseCircle className="w-5 h-5 text-yellow-400" />
                  Suspend Member
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {actionDialog.type === 'ban' ? (
                <>
                  Are you sure you want to ban{' '}
                  <span className="text-white font-medium">
                    {actionDialog.member?.username}
                  </span>
                  ? They will not be able to rejoin the community.
                </>
              ) : (
                <>
                  Are you sure you want to suspend{' '}
                  <span className="text-white font-medium">
                    {actionDialog.member?.username}
                  </span>
                  ? They can view content but cannot post or comment.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-300">
                Reason (optional)
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a reason for this action..."
                className="mt-2 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setActionDialog({ open: false, type: null, member: null })
              }
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={actionDialog.type === 'ban' ? handleBan : handleSuspend}
              disabled={banMutation.isPending || suspendMutation.isPending}
              className={
                actionDialog.type === 'ban'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-yellow-600 hover:bg-yellow-700'
              }
            >
              {(banMutation.isPending || suspendMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {actionDialog.type === 'ban' ? 'Ban Member' : 'Suspend Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
